import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import S3FileUpload from "@/components/common/inputs/S3FileUpload";
import { toast } from "react-toastify";
import {
  SelectedDocument,
  AcceptedFileTypes,
} from "@/components/common/inputs/FileUpload";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";
import DocumentsTable from "./T4OnBoardingDocument/UploadOnBoardingDocument/DocumentsTable";

import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import InfoIcon from "@styles/all-images/icons/info.svg";
import TimeBlue from "@styles/all-images/icons/timeblue.svg";
import MsgBlue from "@styles/all-images/icons/msgblue.svg";
import UploadBlue from "@styles/all-images/icons/uploadblue.svg";
import dateHandler from "@/composables/dateUtil";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
  getDocumentApprovalStatusStyle,
} from "@/constants/enums/document-approval-status.enum";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import ViewTimeLineDocument from "@/pages/T4/T4OnBoardingDocument/TimeLineOnBoardingDocumnet/ViewTimeLineDocument";

export interface Document {
  id: number;
  document_name: string;
  created_at: string;
  updated_at: string;
  uploaded_document: any;
  status: DocumentApprovalStatus;
}

interface DocumentMasterTabProps {
  id: number;
  title: string;
  description: string;
  sequence: number;
  folder_id: number;
  documents: Document[];
  forceRefresh: boolean;
  setForceRefresh: (forceRefresh: boolean) => void;
  isTableView?: boolean;
  isTimeline?: boolean;
}

export interface DocumentMasterTabRef {
  validateDocuments: () => boolean;
}

const DocumentMasterTab = forwardRef<
  DocumentMasterTabRef,
  DocumentMasterTabProps
>(
  (
    {
      title,
      description,
      folder_id,
      documents,
      forceRefresh,
      setForceRefresh,
      isTableView = false,
      isTimeline = false,
    },
    ref,
  ) => {
    const [isNA, setIsNA] = useState<Record<string, boolean>>({});
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, boolean>>(
      {},
    );
    // Expose validation method to parent
    useImperativeHandle(ref, () => ({
      validateDocuments: () => {
        const invalidDocuments = documents.filter((doc) => {
          // Document is invalid if it's neither uploaded nor marked as N/A
          return (
            !uploadedFiles[doc.id] &&
            !isNA[doc.id] &&
            !doc.uploaded_document?.id
          );
        });

        if (invalidDocuments.length > 0) {
          const documentNames = invalidDocuments
            .map((doc) => doc.document_name)
            .join(", ");
          toast.warning(`Please upload or mark as N/A: ${documentNames}`);
          return false;
        }

        return true;
      },
    }));

    const renderUploadDocuments = useCallback(() => {
      return (
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-6">
          {documents?.map((doc) => (
            <S3FileUpload
              key={doc.document_name + doc.id}
              label={doc.document_name}
              folderId={folder_id}
              accept={[
                AcceptedFileTypes.PDF,
                AcceptedFileTypes.XLSX,
                AcceptedFileTypes.CSV,
                AcceptedFileTypes.DOCX,
                AcceptedFileTypes.PNG,
                AcceptedFileTypes.JPG,
                AcceptedFileTypes.SVG,
              ]}
              documentTypeId={doc.id}
              id={`fileInput-${doc.id}`}
              isNA={isNA[doc.id]}
              selectedDocument={doc.uploaded_document}
              onNAChange={(value) => {
                setIsNA((prev) => ({ ...prev, [doc.id]: value }));
                // Reset uploaded status when marked as N/A
                if (value) {
                  setUploadedFiles((prev) => ({ ...prev, [doc.id]: false }));
                }
              }}
              onUploadComplete={(fileData) => {
                setUploadedFiles((prev) => ({ ...prev, [doc.id]: true }));
                setForceRefresh(true);
              }}
              onUploadError={(error) => {
                setUploadedFiles((prev) => ({ ...prev, [doc.id]: false }));
                setForceRefresh(true);
              }}
              setForceRefresh={setForceRefresh}
            />
          ))}
        </div>
      );
    }, [documents, folder_id, setForceRefresh, isNA]);

    const renderTabContent = useCallback(() => {
      switch (true) {
        case isTimeline:
          return (
            <>
              <ViewTimeLineDocument documents={documents} />
            </>
          );
        case isTableView:
          return (
            <DocumentsTable
              setForceRefreshMaster={setForceRefresh}
              documents={documents}
            />
          );
        default:
          return renderUploadDocuments();
      }
    }, [isTimeline, isTableView, documents, renderUploadDocuments]);

    return (
      <div>
        <div className="tw-bg-white tw-pb-3 tw-rounded-lg">
          <h2 className="tw-text-md tw-font-semibold">{description}</h2>
        </div>

        <div className="documents-container">{renderTabContent()}</div>
      </div>
    );
  },
);

export default DocumentMasterTab;
